import type { ChangeEvent, InputHTMLAttributes, ReactNode } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import { cn } from "../utils/cx.js";

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  children?: ReactNode;
}

export function Checkbox({
  children,
  className,
  disabled,
  checked,
  defaultChecked,
  name,
  onChange,
}: CheckboxProps) {
  const control = (
    <CheckboxPrimitive.Root
      name={name}
      disabled={disabled}
      checked={checked}
      defaultChecked={defaultChecked}
      // Synthesize the native `.target.checked` shape callers already read.
      onCheckedChange={(next) =>
        onChange?.({ target: { checked: next === true } } as unknown as ChangeEvent<HTMLInputElement>)
      }
      className={cn(
        "peer size-4 shrink-0 rounded-[4px] border border-input shadow-xs outline-none transition-shadow focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground dark:bg-input/30",
        children == null && className,
      )}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        <Check className="size-3.5" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (children == null) return control;
  return (
    <label
      className={cn(
        "inline-flex items-center gap-2 text-sm",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      {control}
      <span>{children}</span>
    </label>
  );
}
